import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { LoginPage } from './components/LoginPage';
import { AdminLoginPage } from './components/AdminLoginPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import { DashboardStats } from './components/DashboardStats';
import { BudgetMonitor } from './components/BudgetMonitor';
import { TopSpikesTable } from './components/TopSpikesTable';
import { GeoFilter } from './components/GeoFilter';
import { BrandSearch } from './components/BrandSearch';
import { useAuth } from './context/AuthContext';
const EntityDetailModal = lazy(() => import('./components/EntityDetailModal').then(m => ({default: m.EntityDetailModal})));
import { BudgetPage } from './components/BudgetPage';
import { SettingsPage } from './components/SettingsPage';
const CabinetPage = lazy(() => import('./components/CabinetPage').then(m => ({default: m.CabinetPage})));
const BrandDetailPage = lazy(() => import('./components/BrandDetailPage').then(m => ({default: m.BrandDetailPage})));
const UnsubscribePage = lazy(() => import('./components/UnsubscribePage').then(m => ({default: m.UnsubscribePage})));
import { apiService } from './services/api';
import { ArrowUpDown, Download, Loader2 } from 'lucide-react';
import './App.css';

const DEFAULT_FILTERS = {
  minPulse: 0,
  maxPulse: 100,
  minMomentum: -5,
  minBreadth: 0,
  minNpi: 0,
  casinoConfirmation: 'all', // all | confirmed | confirmed_or_likely | unconfirmed | not_gambling
  riskLevel: 'any',      // any | not_high | low | moderate | high
  verification: 'all',   // all | verified | likely | unverified
  hasSite: 'any',       // any | true | false
  hasLicense: 'any',    // any | true | false
  hasSerp: 'any',       // any | true | false
  hasTrustpilot: 'any', // any | true | false
  anomalyOnly: false,
  sortKey: 'spikes',
  sortDirection: 'desc',
};

function DashboardPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const [topSpikes, setTopSpikes] = useState([]);
  const [totalSpikes, setTotalSpikes] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [selectedGeo, setSelectedGeo] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(14);
  const [entityTypeFilter, setEntityTypeFilter] = useState(null);
  const [qualityPreset, setQualityPreset] = useState('all'); // all | balanced | strict | custom
  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [entityTimeline, setEntityTimeline] = useState([]);
  const [error, setError] = useState(null);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportingAllCsv, setExportingAllCsv] = useState(false);
  const [trackModalOpen, setTrackModalOpen] = useState(false);
  const [trackName, setTrackName] = useState('');
  const [trackRegions, setTrackRegions] = useState([]);
  const [trackSendDaily, setTrackSendDaily] = useState(true);
  const [trackingFilter, setTrackingFilter] = useState(false);
  const [trackMessage, setTrackMessage] = useState('');
  const [csvErrorMessage, setCsvErrorMessage] = useState('');
  const [dashboardFocus, setDashboardFocus] = useState('spikes');
  const [spikesScope, setSpikesScope] = useState('spikes'); // spikes | active | alltime | new

  const parseNumberInput = (raw, fallback = 0) => {
    const normalized = String(raw ?? '').trim().replace(',', '.');
    if (!normalized) return fallback;
    const n = Number(normalized);
    return Number.isFinite(n) ? n : fallback;
  };

  const parseTriState = (raw) => {
    if (raw === 'true' || raw === '1') return 'true';
    if (raw === 'false' || raw === '0') return 'false';
    return 'any';
  };

  const triToBool = (raw) => {
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return undefined;
  };

  const makeApiFilters = (filters, opts = {}) => ({
    min_pulse: filters.minPulse,
    max_pulse: filters.maxPulse,
    min_momentum: filters.minMomentum,
    min_breadth: filters.minBreadth,
    min_npi: filters.minNpi > 0 ? filters.minNpi : undefined,
    casino_confirmation: filters.casinoConfirmation !== 'all' ? filters.casinoConfirmation : undefined,
    risk_level: filters.riskLevel !== 'any' ? filters.riskLevel : undefined,
    has_site: triToBool(filters.hasSite),
    has_license: triToBool(filters.hasLicense),
    has_serp: triToBool(filters.hasSerp),
    has_trustpilot: triToBool(filters.hasTrustpilot),
    anomaly_only: opts.disableAnomaly ? undefined : (filters.anomalyOnly || undefined),
    sort_key: filters.sortKey,
    sort_dir: filters.sortDirection,
  });

  const getCsvExportErrorMessage = (err, isAllExport = false) => {
    const details = String(err?.response?.data?.detail || err?.message || '').toLowerCase();
    const status = Number(err?.response?.status || 0);
    const isTimeout = (
      err?.code === 'ECONNABORTED' ||
      details.includes('timeout') ||
      details.includes('timed out') ||
      status === 504
    );

    if (isTimeout) {
      return isAllExport
        ? 'Export All CSV timed out. Try narrowing filters/period/GEO and run export again.'
        : 'CSV export timed out. Try again or use a smaller result set.';
    }
    return isAllExport ? 'Export All CSV failed. Please try again.' : 'CSV export failed. Please try again.';
  };

  const getListVerification = useCallback(() => (
    entityTypeFilter ||
    (appliedFilters.verification || 'all')
  ), [entityTypeFilter, appliedFilters.verification]);

  const getListSortKey = useCallback(() => {
    const listSortMap = {
      spikes: 'score',
      growth: 'momentum',
      score: 'score',
      pulse: 'pulse',
      momentum: 'momentum',
      breadth: 'breadth',
    };
    return listSortMap[appliedFilters.sortKey] || 'score';
  }, [appliedFilters.sortKey]);

  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const daysParam = Number(qs.get('days'));
    const pageParam = Number(qs.get('page'));
    const pageSizeParam = Number(qs.get('page_size'));
    const initial = {
      minPulse: parseNumberInput(qs.get('min_pulse'), DEFAULT_FILTERS.minPulse),
      maxPulse: parseNumberInput(qs.get('max_pulse'), DEFAULT_FILTERS.maxPulse),
      minMomentum: parseNumberInput(qs.get('min_momentum'), DEFAULT_FILTERS.minMomentum),
      minBreadth: parseNumberInput(qs.get('min_breadth'), DEFAULT_FILTERS.minBreadth),
      minNpi: parseNumberInput(qs.get('min_npi'), DEFAULT_FILTERS.minNpi),
      casinoConfirmation: qs.get('casino_confirmation') || DEFAULT_FILTERS.casinoConfirmation,
      riskLevel: qs.get('risk_level') || DEFAULT_FILTERS.riskLevel,
      verification: qs.get('entity_type') || DEFAULT_FILTERS.verification,
      hasSite: parseTriState(qs.get('has_site')),
      hasLicense: parseTriState(qs.get('has_license')),
      hasSerp: parseTriState(qs.get('has_serp')),
      hasTrustpilot: parseTriState(qs.get('has_trustpilot')),
      anomalyOnly: qs.get('anomaly_only') === 'true' || qs.get('anomaly_only') === '1',
      sortKey: qs.get('sort_key') || DEFAULT_FILTERS.sortKey,
      sortDirection: (qs.get('sort_dir') || DEFAULT_FILTERS.sortDirection).toLowerCase() === 'asc' ? 'asc' : 'desc',
    };
    const geoParam = qs.get('geo');
    const typeParam = initial.verification === 'all' ? null : initial.verification;
    setSelectedGeo(geoParam || null);
    setEntityTypeFilter(typeParam || null);
    if ([7, 14, 30, 60, 90].includes(daysParam)) setSelectedPeriod(daysParam);
    if (Number.isFinite(pageParam) && pageParam >= 1) setCurrentPage(Math.floor(pageParam));
    if ([50, 100, 200].includes(pageSizeParam)) setPageSize(pageSizeParam);
    setDraftFilters(initial);
    setAppliedFilters(initial);
  }, []);

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod, selectedGeo]);

  useEffect(() => {
    if (!loading) {
      loadTopSpikes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGeo, entityTypeFilter, selectedPeriod, loading, appliedFilters, spikesScope, currentPage, pageSize]);

  useEffect(() => {
    const qs = new URLSearchParams();
    qs.set('days', String(selectedPeriod));
    if (selectedGeo) qs.set('geo', selectedGeo);
    if (entityTypeFilter) qs.set('entity_type', entityTypeFilter);
    qs.set('min_pulse', String(appliedFilters.minPulse));
    qs.set('max_pulse', String(appliedFilters.maxPulse));
    qs.set('min_momentum', String(appliedFilters.minMomentum));
    qs.set('min_breadth', String(appliedFilters.minBreadth));
    if (appliedFilters.minNpi > 0) qs.set('min_npi', String(appliedFilters.minNpi));
    if (appliedFilters.casinoConfirmation !== 'all') qs.set('casino_confirmation', appliedFilters.casinoConfirmation);
    if (appliedFilters.riskLevel !== 'any') qs.set('risk_level', appliedFilters.riskLevel);
    if (appliedFilters.hasSite !== 'any') qs.set('has_site', appliedFilters.hasSite);
    if (appliedFilters.hasLicense !== 'any') qs.set('has_license', appliedFilters.hasLicense);
    if (appliedFilters.hasSerp !== 'any') qs.set('has_serp', appliedFilters.hasSerp);
    if (appliedFilters.hasTrustpilot !== 'any') qs.set('has_trustpilot', appliedFilters.hasTrustpilot);
    if (appliedFilters.anomalyOnly) qs.set('anomaly_only', 'true');
    qs.set('sort_key', appliedFilters.sortKey);
    qs.set('sort_dir', appliedFilters.sortDirection);
    qs.set('page', String(currentPage));
    qs.set('page_size', String(pageSize));
    window.history.replaceState(null, '', `/?${qs.toString()}`);
  }, [selectedPeriod, selectedGeo, entityTypeFilter, appliedFilters, currentPage, pageSize]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsRes, budgetRes] = await Promise.all([
        apiService.getDashboardStats(selectedPeriod, selectedGeo),
        apiService.getBudgetStatus(),
      ]);

      setDashboardStats(statsRes.data);
      setBudgets(budgetRes.data.budgets);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  const loadTopSpikes = async () => {
    try {
      const limit = pageSize;
      const skip = (currentPage - 1) * pageSize;
      if (spikesScope === 'active') {
        const listVerification = getListVerification();
        const listSortKey = getListSortKey();
        const response = await apiService.getEntitiesList(
          selectedGeo,
          selectedPeriod,
          listVerification,
          'active',
          listSortKey,
          appliedFilters.sortDirection,
          limit,
          skip
        );
        const items = response.data.items || [];
        const total = Number(response.data.total || items.length || 0);
        setTopSpikes(prioritizeByDataCompleteness(items, spikesScope, appliedFilters));
        setTotalSpikes(total);
        return;
      }
      if (spikesScope === 'new') {
        const listVerification = getListVerification();
        const response = await apiService.getEntitiesList(
          selectedGeo,
          selectedPeriod,
          listVerification,
          'new',
          appliedFilters.sortKey || 'score',
          appliedFilters.sortDirection || 'desc',
          limit,
          skip,
          makeApiFilters(appliedFilters, { disableAnomaly: true })
        );
        const items = response.data.items || [];
        const total = Number(response.data.total || items.length || 0);
        setTopSpikes(prioritizeByDataCompleteness(items, spikesScope, appliedFilters));
        setTotalSpikes(total);
        return;
      }
      if (spikesScope === 'alltime') {
        const listVerification = getListVerification();
        const response = await apiService.searchEntities('', selectedGeo, limit, skip, listVerification, 20000);
        const rows = response?.data?.results || [];
        const total = Number(response?.data?.total || rows.length || 0);
        const items = rows.map(mapSearchResultToListItem);
        setTopSpikes(prioritizeByDataCompleteness(items, spikesScope, appliedFilters));
        setTotalSpikes(total);
        return;
      }

      const response = await apiService.getTopSpikes(
        selectedGeo,
        limit,
        skip,
        entityTypeFilter,
        selectedPeriod,
        makeApiFilters(appliedFilters),
        spikesScope
      );
      const items = response.data.items || response.data || [];
      const apiTotal = Number(response.data.total || 0);
      const normalizedTotal = Math.max(apiTotal, items.length);
      setTopSpikes(items);
      setTotalSpikes(normalizedTotal);
    } catch (err) {
      console.error('Error loading spikes:', err);
    }
  };

  const escapeCsv = (val) => {
    const s = String(val ?? '');
    if (s.includes('"') || s.includes(',') || s.includes('\n')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const csvHeaders = [
    'rank',
    'brand',
    'official_name',
    'niche',
    'verification_status',
    'geo',
    'spike_type',
    'spike_score',
    'growth_pct',
    'pulse',
    'momentum',
    'breadth',
    'npi',
    'risk_level',
    'current_score',
    'confidence_multiplier',
    'search_volume',
    'has_site',
    'official_domain',
    'has_license',
    'has_serp',
    'has_trustpilot',
    'date',
  ];

  const toCsvLines = (rows, offset = 0) => {
    const lines = [csvHeaders.join(',')];
    rows.forEach((item, idx) => {
      const pbn = item?.pbn || {};
      const lic = item?.license || {};
      const row = [
        offset + idx + 1,
        item?.entity?.canonical_name || '',
        item?.entity?.official_name || '',
        item?.entity?.niche || '',
        item?.entity?.verification_status || '',
        item?.spike?.geo || '',
        item?.spike?.spike_type || '',
        item?.spike?.score ?? '',
        item?.spike?.growth_percentage ?? '',
        item?.pulse ?? '',
        item?.momentum ?? '',
        item?.breadth ?? item?.sources_active ?? '',
        pbn?.npi ?? '',
        pbn?.risk_level ?? '',
        item?.current_score ?? '',
        item?.confidence_multiplier ?? '',
        item?.search_volume ?? '',
        item?.official_domain ? 'true' : 'false',
        item?.official_domain || '',
        lic?.has_license ? 'true' : 'false',
        item?.has_serp ? 'true' : 'false',
        item?.has_trustpilot ? 'true' : 'false',
        item?.spike?.date || '',
      ].map(escapeCsv);
      lines.push(row.join(','));
    });
    return lines;
  };

  const downloadCsv = (lines, prefix) => {
    const csv = '\uFEFF' + lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
    a.href = url;
    a.download = `${prefix}_${selectedGeo || 'all'}_${appliedFilters.sortKey}_${appliedFilters.sortDirection}_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const fetchRowsForExport = async (limit, skip) => {
    if (spikesScope === 'active') {
      const response = await apiService.getEntitiesList(
        selectedGeo,
        selectedPeriod,
        getListVerification(),
        'active',
        getListSortKey(),
        appliedFilters.sortDirection,
        limit,
        skip
      );
      return { rows: response?.data?.items || [], total: Number(response?.data?.total || 0) };
    }
    if (spikesScope === 'new') {
      const response = await apiService.getEntitiesList(
        selectedGeo,
        selectedPeriod,
        getListVerification(),
        'new',
        appliedFilters.sortKey || 'score',
        appliedFilters.sortDirection || 'desc',
        limit,
        skip,
        makeApiFilters(appliedFilters, { disableAnomaly: true })
      );
      return { rows: response?.data?.items || [], total: Number(response?.data?.total || 0) };
    }
    // Выгрузка должна отдавать ровно то, что на экране. Раньше страна здесь
    // была прибита к null, и при выбранном Кипре скачивался весь список; а в
    // режимах «активные» и «новые» данные брались вообще из другого
    // эндпоинта — не из того, что показано.
    if (spikesScope === 'alltime') {
      const response = await apiService.searchEntities('', selectedGeo, limit, skip, getListVerification(), 30000);
      const raw = response?.data?.results || [];
      return { rows: raw.map(mapSearchResultToListItem), total: Number(response?.data?.total || raw.length || 0) };
    }
    if (spikesScope === 'active' || spikesScope === 'new') {
      const response = await apiService.getEntitiesList(
        selectedGeo,
        selectedPeriod,
        getListVerification(),
        spikesScope,
        appliedFilters.sortKey || 'score',
        appliedFilters.sortDirection || 'desc',
        limit,
        skip,
        spikesScope === 'new' ? makeApiFilters(appliedFilters, { disableAnomaly: true })
                              : makeApiFilters(appliedFilters)
      );
      const rows = response?.data?.items || [];
      return { rows, total: Number(response?.data?.total || rows.length || 0) };
    }
    const response = await apiService.getTopSpikes(
      selectedGeo,
      Math.min(limit, 100),
      skip,
      entityTypeFilter,
      selectedPeriod,
      makeApiFilters(appliedFilters),
      spikesScope
    );
    const rows = response?.data?.items || response?.data || [];
    return { rows, total: Number(response?.data?.total || rows.length || 0) };
  };

  const exportTop100Csv = async () => {
    try {
      setExportingCsv(true);
      setCsvErrorMessage('');
      const { rows } = await fetchRowsForExport(100, 0);
      downloadCsv(toCsvLines(rows.slice(0, 100)), 'top100');
    } catch (e) {
      console.error('Export CSV failed:', e);
      setCsvErrorMessage(getCsvExportErrorMessage(e, false));
    } finally {
      setExportingCsv(false);
    }
  };

  const exportAllCsv = async () => {
    try {
      setExportingAllCsv(true);
      setCsvErrorMessage('');
      const chunkSize = spikesScope === 'spikes' ? 100 : 500;
      let skip = 0;
      let total = 0;
      let allRows = [];
      while (true) {
        const { rows, total: fetchedTotal } = await fetchRowsForExport(chunkSize, skip);
        if (skip === 0) total = fetchedTotal;
        if (!rows.length) break;
        allRows = allRows.concat(rows);
        skip += rows.length;
        if (allRows.length >= total) break;
      }
      downloadCsv(toCsvLines(allRows), 'export_all');
    } catch (e) {
      console.error('Export all CSV failed:', e);
      setCsvErrorMessage(getCsvExportErrorMessage(e, true));
    } finally {
      setExportingAllCsv(false);
    }
  };

  const handleEntityClick = async (entityId) => {
    try {
      const [detailsRes, timelineRes] = await Promise.all([
        apiService.getEntityDetails(entityId),
        apiService.getEntityTimeline(entityId, selectedGeo, selectedPeriod),
      ]);

      setSelectedEntity(detailsRes.data);
      setEntityTimeline(timelineRes.data.data_points);
    } catch (err) {
      console.error('Error loading entity details:', err);
    }
  };

  const handleCloseModal = () => {
    setSelectedEntity(null);
    setEntityTimeline([]);
  };

  const applyQualityPreset = (preset) => {
    setQualityPreset(preset);
    setDraftFilters(prev => {
      if (preset === 'all') {
        return { ...prev, minPulse: 0, maxPulse: 100, minMomentum: -5, minBreadth: 0 };
      }
      if (preset === 'strict') {
        return { ...prev, minPulse: 70, maxPulse: 100, minMomentum: 1.0, minBreadth: 4 };
      }
      return { ...prev, minPulse: 60, maxPulse: 100, minMomentum: 0.5, minBreadth: 3 };
    });
  };

  const applyFilters = () => {
    const normalized = { ...draftFilters };
    if (normalized.minPulse > normalized.maxPulse) {
      const tmp = normalized.minPulse;
      normalized.minPulse = normalized.maxPulse;
      normalized.maxPulse = tmp;
    }
    normalized.minPulse = Math.max(0, Math.min(100, normalized.minPulse));
    normalized.maxPulse = Math.max(0, Math.min(100, normalized.maxPulse));
    setAppliedFilters(normalized);
    setDraftFilters(normalized);
    const ver = normalized.verification;
    setEntityTypeFilter(ver === 'all' ? null : ver);
    setCurrentPage(1);
  };

  const setFiltersAndReload = (nextFilters, nextEntityType = null) => {
    setDraftFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setEntityTypeFilter(nextEntityType);
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setQualityPreset('all');
    setDraftFilters(DEFAULT_FILTERS);
    setSpikesScope('spikes');
    setDashboardFocus('spikes');
  };

  const isBroadAllFilters = (filters) => (
    (filters?.casinoConfirmation || 'all') === 'all' &&
    (filters?.riskLevel || 'any') === 'any' &&
    (filters?.verification || 'all') === 'all' &&
    (filters?.hasSite || 'any') === 'any' &&
    (filters?.hasLicense || 'any') === 'any' &&
    (filters?.hasSerp || 'any') === 'any' &&
    (filters?.hasTrustpilot || 'any') === 'any' &&
    Number(filters?.minPulse ?? 0) <= 0 &&
    Number(filters?.minBreadth ?? 0) <= 0 &&
    Number(filters?.minNpi ?? 0) <= 0 &&
    Number(filters?.minMomentum ?? -5) <= -5
  );

  const getDataCompletenessScore = (item) => {
    const breadth = Number(item?.breadth ?? item?.sources_active ?? 0);
    const score = Number(item?.current_score ?? item?.spike?.score ?? 0);
    const volume = Number(item?.search_volume ?? 0);
    const reviewSignals = Number(item?.review_signals_count ?? 0);
    const hasSerp = item?.has_serp ? 1 : 0;
    const hasSite = item?.entity?.official_domain ? 1 : 0;
    const hasLicense = item?.license?.has_license ? 1 : 0;
    const conf = Number(item?.confidence_multiplier ?? 1);
    const pulse = Number(item?.pulse ?? 0);

    return (
      (breadth * 20) +
      (score * 2) +
      Math.min(volume / 10, 120) +
      (reviewSignals * 12) +
      (hasSerp * 80) +
      (hasSite * 50) +
      (hasLicense * 60) +
      (Math.max(conf - 1, 0) * 20) +
      pulse
    );
  };

  const prioritizeByDataCompleteness = (items, scope, filters) => {
    if (!Array.isArray(items) || items.length <= 1) return items || [];
    const shouldPrioritize =
      scope === 'new' ||
      (scope === 'active' && isBroadAllFilters(filters)) ||
      (scope === 'alltime' && isBroadAllFilters(filters));
    if (!shouldPrioritize) return items;

    return [...items].sort((a, b) => {
      const diff = getDataCompletenessScore(b) - getDataCompletenessScore(a);
      if (diff !== 0) return diff;
      const da = new Date(a?.spike?.date || a?.entity?.first_seen_date || 0).getTime() || 0;
      const db = new Date(b?.spike?.date || b?.entity?.first_seen_date || 0).getTime() || 0;
      return db - da;
    });
  };

  const mapSearchResultToListItem = (row) => {
    const baseScore = Number(row?.total_score_7d || 0);
    return {
      entity: {
        id: row?.id,
        canonical_name: row?.canonical_name || 'Unknown',
        official_name: row?.official_name || null,
        official_domain: row?.official_domain || null,
        niche: row?.niche || 'casino',
        first_seen_geo: row?.first_seen_geo || 'ALL',
        first_seen_date: row?.first_seen_date || new Date().toISOString(),
        verification_status: row?.verification_status || 'unknown',
        entity_type: row?.entity_type || 'unknown',
      },
      spike: {
        geo: selectedGeo || row?.first_seen_geo || 'ALL',
        date: row?.first_seen_date || new Date().toISOString(),
        spike_type: 'signal',
        growth_percentage: 0,
        score: baseScore,
      },
      pulse: Number(row?.pulse || 0),
      momentum: 0,
      breadth: 0,
      current_score: baseScore,
      mini_timeline: [baseScore],
      signal_breakdown: {},
      sources_active: 0,
      confidence_multiplier: 1.0,
      search_volume: 0,
      has_serp: false,
      has_trustpilot: false,
      regulators: [],
      review_signals_count: 0,
      review_signals: [],
      pbn: null,
      license: null,
    };
  };

  const showActiveVerifiedBrands = () => {
    const broadVerified = {
      ...DEFAULT_FILTERS,
      minPulse: 0,
      minMomentum: -5,
      minBreadth: 0,
      verification: 'verified',
      sortKey: 'score',
      sortDirection: 'desc',
    };
    setSelectedGeo(null);
    setQualityPreset('all');
    setSpikesScope('active');
    setFiltersAndReload(broadVerified, 'verified');
    setDashboardFocus('entities');
  };

  const showAllTimeVerifiedBrands = () => {
    const broadVerified = {
      ...DEFAULT_FILTERS,
      minPulse: 0,
      minMomentum: -5,
      minBreadth: 0,
      verification: 'verified',
      sortKey: 'score',
      sortDirection: 'desc',
    };
    setSelectedGeo(null);
    setQualityPreset('all');
    setSpikesScope('alltime');
    setFiltersAndReload(broadVerified, 'verified');
    setDashboardFocus('entities');
  };

  const showNewBrands = () => {
    if (spikesScope === 'new') {
      setDraftFilters((prev) => ({ ...prev, anomalyOnly: false }));
      setAppliedFilters((prev) => ({ ...prev, anomalyOnly: false }));
      setDashboardFocus('newbrands');
      return;
    }
    const broadNew = {
      ...DEFAULT_FILTERS,
      verification: 'all',
      anomalyOnly: false,
      sortKey: 'score',
      sortDirection: 'desc',
    };
    setQualityPreset('all');
    setSpikesScope('new');
    setFiltersAndReload(broadNew, null);
    setDashboardFocus('newbrands');
  };

  const applyRestrictedVerification = (verificationValue) => {
    const normalized = verificationValue || 'verified';
    setDraftFilters(prev => ({ ...prev, verification: normalized }));
    setAppliedFilters(prev => ({ ...prev, verification: normalized }));
    setEntityTypeFilter(normalized === 'all' ? null : normalized);
    setCurrentPage(1);
  };

  const handleDashboardMetricClick = (metricKey) => {
    const topGeo = dashboardStats?.top_growing_geo;

    if (metricKey === 'entities') {
      showAllTimeVerifiedBrands();
      return;
    }

    if (metricKey === 'spikes') {
      const spikeFilters = {
        ...DEFAULT_FILTERS,
        minPulse: 0,
        maxPulse: 100,
        minMomentum: -5,
        minBreadth: 0,
        verification: 'all',
        sortKey: 'spikes',
        sortDirection: 'desc',
      };
      setQualityPreset('all');
      setSpikesScope('spikes');
      setFiltersAndReload(spikeFilters, null);
      setDashboardFocus('spikes');
      return;
    }

    if (metricKey === 'geos') {
      setSelectedGeo(null);
      setSpikesScope('spikes');
      setDashboardFocus('geos');
      return;
    }

    if (metricKey === 'newbrands') {
      showNewBrands();
      return;
    }

    if (metricKey === 'topgeo') {
      if (topGeo && topGeo !== 'N/A') {
        setSelectedGeo(topGeo);
      }
      setSpikesScope('spikes');
      setDashboardFocus('topgeo');
    }
  };

  const openTrackModal = () => {
    setTrackName(`Decision ${selectedGeo || 'All'} ${selectedPeriod}d`);
    setTrackRegions(selectedGeo ? [selectedGeo] : []);
    setTrackSendDaily(true);
    setTrackMessage('');
    setTrackModalOpen(true);
  };

  const toggleTrackRegion = (code) => {
    setTrackRegions(prev => (
      prev.includes(code) ? prev.filter(g => g !== code) : [...prev, code]
    ));
  };

  const createTrackedFilter = async () => {
    const name = trackName.trim();
    if (!name) {
      setTrackMessage('Введите название фильтра');
      return;
    }
    try {
      setTrackingFilter(true);
      const payload = {
        name,
        send_daily: trackSendDaily,
        regions: trackRegions,
        query_payload: {
          geo: selectedGeo || null,
          entity_type: entityTypeFilter || null,
          days: selectedPeriod,
          min_pulse: appliedFilters.minPulse,
          max_pulse: appliedFilters.maxPulse,
          min_momentum: appliedFilters.minMomentum,
          min_breadth: appliedFilters.minBreadth,
          min_npi: appliedFilters.minNpi > 0 ? appliedFilters.minNpi : null,
          casino_confirmation: appliedFilters.casinoConfirmation !== 'all' ? appliedFilters.casinoConfirmation : null,
          risk_level: appliedFilters.riskLevel !== 'any' ? appliedFilters.riskLevel : null,
          has_site: appliedFilters.hasSite !== 'any' ? appliedFilters.hasSite : null,
          has_license: appliedFilters.hasLicense !== 'any' ? appliedFilters.hasLicense : null,
          has_serp: appliedFilters.hasSerp !== 'any' ? appliedFilters.hasSerp : null,
          has_trustpilot: appliedFilters.hasTrustpilot !== 'any' ? appliedFilters.hasTrustpilot : null,
          anomaly_only: appliedFilters.anomalyOnly ? true : null,
          sort_key: appliedFilters.sortKey,
          sort_dir: appliedFilters.sortDirection,
          limit: 100,
        },
      };
      const response = await apiService.createTrackedFilter(payload);
      const savedEmail = response?.data?.filter?.user_email || user?.email || 'ваш email';
      setTrackModalOpen(false);
      setTrackMessage(`Фильтр "${name}" сохранен. Уведомления будут отправляться на ${savedEmail}.`);
      setTimeout(() => setTrackMessage(''), 8000);
    } catch (e) {
      setTrackMessage(e?.response?.data?.detail || 'Не удалось сохранить фильтр');
    } finally {
      setTrackingFilter(false);
    }
  };

  const filteredSpikes = topSpikes;
  const isAllTimeMode = spikesScope === 'alltime';
  const isNewMode = spikesScope === 'new';
  const isRestrictedMode = isAllTimeMode;
  const totalPages = Math.max(1, Math.ceil((totalSpikes || 0) / pageSize));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  if (loading) {
    return (
      <Layout activeTab="dashboard">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
            <p className="text-lg text-muted-foreground">Loading Dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout activeTab="dashboard">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center max-w-md p-8 bg-card border border-border rounded-lg">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl"></span>
            </div>
            <h2 className="text-xl font-display font-bold text-foreground mb-2">Error</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <button
              onClick={loadDashboardData}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
            >
              Retry
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout activeTab="dashboard">
      {/* Global Controls: Period + GEO */}
      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-border">
        <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Period</span>
        <div className="flex bg-card border border-border rounded-lg overflow-hidden">
          {[
            { value: 7, label: '7d' },
            { value: 14, label: '14d' },
            { value: 30, label: '30d' },
            { value: 60, label: '60d' },
            { value: 90, label: '90d' },
          ].map(p => (
            <button
              key={p.value}
              onClick={() => setSelectedPeriod(p.value)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                selectedPeriod === p.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider ml-2">GEO</span>
        <span className="text-sm font-medium text-foreground">{selectedGeo || 'All Regions'}</span>
        <div className="ml-auto flex items-center gap-3 px-2.5 py-1.5 rounded-md border border-emerald-300 bg-emerald-50">
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-emerald-700 font-medium uppercase tracking-wider">
              {spikesScope === 'active'
                ? `Verified active (${selectedPeriod}d${selectedGeo ? `, ${selectedGeo}` : ''})`
                : spikesScope === 'new'
                ? `New brands (${selectedPeriod}d${selectedGeo ? `, ${selectedGeo}` : ''})`
                : spikesScope === 'alltime'
                ? (selectedGeo ? `Verified in ${selectedGeo}` : 'Verified all-time')
                : selectedGeo
                ? 'Verified Casinos (all regions)'
                : 'Verified Casinos'}
            </span>
            <span
              className="text-sm font-semibold text-emerald-700"
              title={
                spikesScope === 'active'
                  ? 'Verified entities for current list mode (period/GEO/filter context)'
                  : spikesScope === 'new'
                  ? 'New brands added to the database in selected period/GEO'
                  : spikesScope === 'alltime'
                  ? (selectedGeo
                      ? `Verified entities with activity in ${selectedGeo}`
                      : 'Verified entities matching the current list filters')
                  : 'Total verified casinos in database — all-time and all regions, not affected by the GEO filter'
              }
            >
              {(
                spikesScope === 'active' || spikesScope === 'alltime' || spikesScope === 'new'
                  ? totalSpikes
                  : (dashboardStats?.verified_casinos_total || 0)
              ).toLocaleString()}
            </span>
          </div>
          {spikesScope === 'active' && (
            <div className="pl-3 border-l border-emerald-300 text-[11px] text-emerald-700">
              All-time: <span className="font-semibold text-emerald-700">{(dashboardStats?.verified_casinos_total || 0).toLocaleString()}</span>
            </div>
          )}
        </div>
      </div>

      <div className="mb-8">
        <DashboardStats
          stats={dashboardStats}
          selectedGeo={selectedGeo}
          onGeoChange={(geo) => {
            setSelectedGeo(geo);
            setDashboardFocus(spikesScope === 'active' ? 'entities' : (spikesScope === 'new' ? 'newbrands' : 'spikes'));
          }}
          onMetricClick={handleDashboardMetricClick}
          activeMetric={dashboardFocus}
          selectedPeriod={selectedPeriod}
          spikeListTotal={spikesScope === 'spikes' ? totalSpikes : undefined}
        />
      </div>

      <BrandSearch onEntityClick={handleEntityClick} selectedGeo={selectedGeo} />

      <div className="grid grid-cols-1 xl:grid-cols-5 2xl:grid-cols-6 gap-4 mb-8 items-start">
        <div className="xl:col-span-1 2xl:col-span-1">
          <GeoFilter
            selectedGeo={selectedGeo}
            onGeoChange={(geo) => {
              setSelectedGeo(geo);
              setDashboardFocus(spikesScope === 'active' ? 'entities' : (spikesScope === 'new' ? 'newbrands' : 'spikes'));
            }}
            geosWithData={dashboardStats?.geos_with_data || []}
            geosWithSpikes={dashboardStats?.geos_with_spikes || []}
            geoSpikeCounts={dashboardStats?.geo_spike_counts || {}}
          />
        </div>

        <div className="xl:col-span-4 2xl:col-span-5 min-w-0">
          <div className="mb-4">
            <h2 className="text-2xl font-display font-bold text-foreground">
              {spikesScope === 'active'
                ? (selectedGeo ? `Active Verified Brands in ${selectedGeo}` : 'Active Verified Brands - All Regions')
                : spikesScope === 'new'
                ? (selectedGeo ? `New Brands in ${selectedGeo}` : 'New Brands - All Regions')
                : spikesScope === 'alltime'
                ? 'All Verified Brands (all-time DB)'
                : (selectedGeo ? `Top Spikes in ${selectedGeo}` : 'Top Spikes - All Regions')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {spikesScope === 'active'
                ? `Database list mode: active verified brands for the last ${selectedPeriod} days`
                : spikesScope === 'new'
                ? `Database list mode: new brands added in the last ${selectedPeriod} days`
                : spikesScope === 'alltime'
                ? 'Database list mode: all verified brands across all time (verification filter only)'
                : `Entities showing sudden growth patterns in the last ${selectedPeriod} days`}
              {/* Явно проговариваем, по чему фильтрует GEO: строки отбираются по
                  региону СПАЙКА, а не по стране происхождения бренда. Без этого
                  "Origin: IT" в списке по Анголе выглядит как сломанный фильтр. */}
              {selectedGeo && spikesScope === 'spikes' && (
                <span className="block text-[11px] text-muted-foreground font-medium mt-1">
                  Filtered by the region where the spike happened ({selectedGeo}); a brand may originate elsewhere.
                </span>
              )}
              {totalSpikes > 0 && (
                spikesScope === 'spikes'
                  ? ` (${filteredSpikes.length} shown of ${totalSpikes} brands with spikes; ${(dashboardStats?.total_spikes_period || 0).toLocaleString()} spike events)`
                  : ` (${filteredSpikes.length} shown of ${totalSpikes})`
              )}
            </p>
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={() => {
                  setSpikesScope('spikes');
                  setDashboardFocus('spikes');
                }}
                title="Brands with growth/anomaly spike events detected in selected period. Sorted by anomaly first, then by growth %."
                className={`h-8 px-3 rounded-md text-xs border ${
                  spikesScope === 'spikes'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background border-border text-muted-foreground hover:text-foreground'
                }`}
              >
                Top Spikes (events)
              </button>
              <button
                onClick={showActiveVerifiedBrands}
                title="All verified brands that have signal activity in the selected period. Includes scores, sources, and quality metrics."
                className={`h-8 px-3 rounded-md text-xs border ${
                  spikesScope === 'active'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background border-border text-muted-foreground hover:text-foreground'
                }`}
              >
                Active Verified Brands (period DB)
              </button>
              <button
                onClick={showAllTimeVerifiedBrands}
                title="Complete list of all verified brands across all time. Period/signal filters are disabled — only verification filter applies."
                className={`h-8 px-3 rounded-md text-xs border ${
                  spikesScope === 'alltime'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background border-border text-muted-foreground hover:text-foreground'
                }`}
              >
                All Verified Brands (all-time DB)
              </button>
              <button
                onClick={showNewBrands}
                title="Recently discovered brands — first seen within the selected period. Data-rich entries shown first."
                className={`h-8 px-3 rounded-md text-xs border ${
                  spikesScope === 'new'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background border-border text-muted-foreground hover:text-foreground'
                }`}
              >
                New Brands (period DB)
              </button>
            </div>
            <div className={`mt-3 p-4 rounded-xl bg-card border ${isRestrictedMode ? 'border-amber-300' : 'border-border'}`}>
              {isRestrictedMode && (
                <div className="mb-3 p-2.5 rounded-md bg-amber-50 border border-amber-300">
                  <div className="text-[11px] text-amber-700 mb-2">
                    All-time mode uses database-wide list. Signal-quality filters are disabled here.
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-muted-foreground">Verification</span>
                    <select
                      value={draftFilters.verification}
                      onChange={(e) => applyRestrictedVerification(e.target.value)}
                      className="h-8 px-2.5 rounded-md bg-background border border-border text-xs"
                    >
                      <option value="all">All</option>
                      <option value="verified">Verified</option>
                      <option value="likely">Likely</option>
                      <option value="unverified">Unverified</option>
                    </select>
                  </div>
                </div>
              )}
              <div className={`flex flex-col gap-4 ${isRestrictedMode ? 'opacity-45 pointer-events-none select-none' : ''}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-foreground mr-1">
                    Dashboard Filters (Apply once = single GET query)
                  </span>
                  {[
                    { key: 'all', label: 'All' },
                    { key: 'balanced', label: 'Balanced' },
                    { key: 'strict', label: 'Strict' },
                  ].map((p) => (
                    <button
                      key={p.key}
                      onClick={() => applyQualityPreset(p.key)}
                      className={`h-7 px-2.5 rounded text-xs font-medium transition-colors ${
                        qualityPreset === p.key
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background border border-border text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-7 2xl:grid-cols-9 gap-2 items-end">
                  <label className="flex flex-col gap-1 min-w-0">
                    <span className="text-[11px] text-muted-foreground">Verification</span>
                    <select
                      value={draftFilters.verification}
                      onChange={(e) => setDraftFilters(prev => ({ ...prev, verification: e.target.value }))}
                      className="h-9 w-full px-2.5 rounded-md bg-background border border-border text-sm"
                    >
                      <option value="all">All</option>
                      <option value="verified">Verified</option>
                      <option value="likely">Likely</option>
                      <option value="unverified">Unverified</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-1 min-w-0">
                    <span className="text-[11px] text-muted-foreground">Min Pulse</span>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={draftFilters.minPulse}
                      onChange={(e) => {
                        setQualityPreset('custom');
                        setDraftFilters(prev => ({ ...prev, minPulse: parseNumberInput(e.target.value, 0) }));
                      }}
                      className="h-9 w-full px-2.5 rounded-md bg-background border border-border text-sm"
                    />
                  </label>
                  <label className="flex flex-col gap-1 min-w-0">
                    <span className="text-[11px] text-muted-foreground">Max Pulse</span>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={draftFilters.maxPulse}
                      onChange={(e) => {
                        setQualityPreset('custom');
                        setDraftFilters(prev => ({ ...prev, maxPulse: parseNumberInput(e.target.value, 100) }));
                      }}
                      className="h-9 w-full px-2.5 rounded-md bg-background border border-border text-sm"
                    />
                  </label>
                  <label className="flex flex-col gap-1 min-w-0">
                    <span className="text-[11px] text-muted-foreground">Min Momentum</span>
                    <input
                      type="number"
                      min="-5"
                      max="5"
                      step="0.1"
                      value={draftFilters.minMomentum}
                      onChange={(e) => {
                        setQualityPreset('custom');
                        setDraftFilters(prev => ({ ...prev, minMomentum: parseNumberInput(e.target.value, 0) }));
                      }}
                      className="h-9 w-full px-2.5 rounded-md bg-background border border-border text-sm"
                    />
                  </label>
                  <label className="flex flex-col gap-1 min-w-0">
                    <span className="text-[11px] text-muted-foreground">Min Breadth</span>
                    <input
                      type="number"
                      min="0"
                      max="8"
                      step="1"
                      value={draftFilters.minBreadth}
                      onChange={(e) => {
                        setQualityPreset('custom');
                        setDraftFilters(prev => ({ ...prev, minBreadth: parseNumberInput(e.target.value, 0) }));
                      }}
                      className="h-9 w-full px-2.5 rounded-md bg-background border border-border text-sm"
                    />
                  </label>
                  <label className="flex flex-col gap-1 min-w-0">
                    <span className="text-[11px] text-muted-foreground">Min NPI</span>
                    <input
                      type="number"
                      min="0"
                      max="12"
                      step="1"
                      value={draftFilters.minNpi}
                      onChange={(e) => setDraftFilters(prev => ({ ...prev, minNpi: parseNumberInput(e.target.value, 0) }))}
                      className="h-9 w-full px-2.5 rounded-md bg-background border border-border text-sm"
                    />
                  </label>
                  <label className="flex flex-col gap-1 min-w-0">
                    <span className="text-[11px] text-muted-foreground">Casino Confirmation</span>
                    <select
                      value={draftFilters.casinoConfirmation}
                      onChange={(e) => setDraftFilters(prev => ({ ...prev, casinoConfirmation: e.target.value }))}
                      className="h-9 w-full px-2.5 rounded-md bg-background border border-border text-sm"
                    >
                      <option value="all">Any</option>
                      <option value="confirmed">Confirmed only</option>
                      <option value="confirmed_or_likely">Confirmed + Likely</option>
                      <option value="unconfirmed">Unconfirmed only</option>
                      <option value="not_gambling">Not Gambling only</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-1 min-w-0">
                    <span className="text-[11px] text-muted-foreground">Risk</span>
                    <select
                      value={draftFilters.riskLevel}
                      onChange={(e) => setDraftFilters(prev => ({ ...prev, riskLevel: e.target.value }))}
                      className="h-9 w-full px-2.5 rounded-md bg-background border border-border text-sm"
                    >
                      <option value="any">Any</option>
                      <option value="not_high">Not High</option>
                      <option value="low">Low</option>
                      <option value="moderate">Moderate</option>
                      <option value="high">High</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-1 min-w-0">
                    <span className="text-[11px] text-muted-foreground">Official Site</span>
                    <select
                      value={draftFilters.hasSite}
                      onChange={(e) => setDraftFilters(prev => ({ ...prev, hasSite: e.target.value }))}
                      className="h-9 w-full px-2.5 rounded-md bg-background border border-border text-sm"
                    >
                      <option value="any">Any</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-1 min-w-0">
                    <span className="text-[11px] text-muted-foreground">Has License</span>
                    <select
                      value={draftFilters.hasLicense}
                      onChange={(e) => setDraftFilters(prev => ({ ...prev, hasLicense: e.target.value }))}
                      className="h-9 w-full px-2.5 rounded-md bg-background border border-border text-sm"
                    >
                      <option value="any">Any</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-1 min-w-0">
                    <span className="text-[11px] text-muted-foreground">Has SERP</span>
                    <select
                      value={draftFilters.hasSerp}
                      onChange={(e) => setDraftFilters(prev => ({ ...prev, hasSerp: e.target.value }))}
                      className="h-9 w-full px-2.5 rounded-md bg-background border border-border text-sm"
                    >
                      <option value="any">Any</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-1 min-w-0">
                    <span className="text-[11px] text-muted-foreground">Trustpilot</span>
                    <select
                      value={draftFilters.hasTrustpilot}
                      onChange={(e) => setDraftFilters(prev => ({ ...prev, hasTrustpilot: e.target.value }))}
                      className="h-9 w-full px-2.5 rounded-md bg-background border border-border text-sm"
                    >
                      <option value="any">Any</option>
                      <option value="true">Found</option>
                      <option value="false">Not found</option>
                    </select>
                  </label>
                  {!isNewMode && (
                    <label className="flex items-center gap-2 h-9 px-2.5 rounded-md border border-border bg-background self-end">
                      <input
                        type="checkbox"
                        checked={draftFilters.anomalyOnly}
                        onChange={(e) => setDraftFilters(prev => ({ ...prev, anomalyOnly: e.target.checked }))}
                        className="w-4 h-4"
                      />
                      <span className="text-[11px] text-muted-foreground whitespace-nowrap">Anomaly only</span>
                    </label>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] text-muted-foreground">Sort</span>
                  <select
                    value={draftFilters.sortKey}
                    onChange={(e) => setDraftFilters(prev => ({ ...prev, sortKey: e.target.value }))}
                    className="h-8 min-w-[160px] px-2.5 rounded-md bg-background border border-border text-xs text-foreground"
                  >
                    <option value="spikes">Spike Score</option>
                    <option value="pulse">Pulse</option>
                    <option value="growth">Growth %</option>
                    <option value="score">Composite Score</option>
                    <option value="momentum">Momentum</option>
                    <option value="breadth">Breadth</option>
                    <option value="npi">NPI</option>
                    <option value="risk">Risk Level</option>
                    <option value="search_volume">Search Volume</option>
                    <option value="confidence">Confidence Multiplier</option>
                  </select>
                  <button
                    onClick={() => setDraftFilters(prev => ({ ...prev, sortDirection: prev.sortDirection === 'desc' ? 'asc' : 'desc' }))}
                    className="h-8 px-2.5 rounded-md bg-background border border-border text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                    title={`Direction: ${draftFilters.sortDirection === 'desc' ? 'DESC' : 'ASC'}`}
                  >
                    <ArrowUpDown className="w-3.5 h-3.5" />
                    {draftFilters.sortDirection.toUpperCase()}
                  </button>
                </div>
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 border-t border-border pt-3">
                  <div className="text-[11px] text-muted-foreground">
                    Composite score is internal ranking (weighted sources × confidence multiplier).
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={applyFilters}
                      className="h-8 px-3 rounded-md text-xs bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      Apply
                    </button>
                    <button
                      onClick={resetFilters}
                      className="h-8 px-3 rounded-md text-xs bg-background border border-border text-muted-foreground hover:text-foreground"
                    >
                      Reset
                    </button>
                    <button
                      onClick={openTrackModal}
                      className="h-8 px-3 rounded-md text-xs bg-background border border-border text-muted-foreground hover:text-foreground"
                      title="Save current applied filters and receive daily updates"
                    >
                      Track this filter
                    </button>
                    <button
                      onClick={exportTop100Csv}
                      disabled={exportingCsv}
                      className="h-8 px-3 rounded-md text-xs bg-background border border-border text-muted-foreground hover:text-foreground disabled:opacity-50 flex items-center gap-1"
                      title="Export current page rows"
                    >
                      <Download className="w-3.5 h-3.5" />
                      {exportingCsv ? 'Exporting...' : 'Export Page CSV'}
                    </button>
                    <button
                      onClick={exportAllCsv}
                      disabled={exportingAllCsv}
                      className="h-8 px-3 rounded-md text-xs bg-background border border-border text-muted-foreground hover:text-foreground disabled:opacity-50"
                      title="Export full filtered dataset to CSV"
                    >
                      {exportingAllCsv ? 'Exporting all...' : 'Export All CSV'}
                    </button>
                  </div>
                </div>
                {trackMessage && (
                  <div className={`text-xs ${trackMessage.toLowerCase().includes('не удалось') || trackMessage.toLowerCase().includes('error') ? 'text-red-700' : 'text-emerald-700'}`}>
                    {trackMessage}
                  </div>
                )}
                {csvErrorMessage && (
                  <div className="text-xs text-red-700">
                    {csvErrorMessage}
                  </div>
                )}
              </div>
            </div>
          </div>
          <TopSpikesTable
            spikes={filteredSpikes}
            onEntityClick={handleEntityClick}
            onSpikesRefresh={loadTopSpikes}
          />
          {totalSpikes > 0 && (
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-xs text-muted-foreground">
                Page {currentPage} of {totalPages} · {totalSpikes.toLocaleString()} total
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Per page</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="h-8 px-2 rounded-md bg-background border border-border text-xs"
                >
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  className="h-8 px-3 rounded-md bg-background border border-border text-xs disabled:opacity-50"
                >
                  Prev
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className="h-8 px-3 rounded-md bg-background border border-border text-xs disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {trackModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="w-full max-w-xl bg-card border border-border rounded-xl p-4">
            <h3 className="text-base font-semibold text-foreground mb-3">Track this filter</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Сохраняет текущие applied filters и включает ежедневный дайджест новых verified брендов.
            </p>
            <label className="flex flex-col gap-1 mb-3">
              <span className="text-xs text-muted-foreground">Filter name</span>
              <input
                value={trackName}
                onChange={(e) => setTrackName(e.target.value)}
                className="h-9 px-2.5 rounded-md bg-background border border-border text-sm"
                placeholder="e.g. High momentum EU"
              />
            </label>
            <div className="mb-3">
              <p className="text-xs text-muted-foreground mb-1">Regions (optional, from active GEO list)</p>
              <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto p-2 rounded-md border border-border bg-background">
                {(dashboardStats?.geos_with_data || []).slice(0, 40).map((geo) => (
                  <button
                    key={geo}
                    type="button"
                    onClick={() => toggleTrackRegion(geo)}
                    className={`px-2 py-1 text-[11px] rounded border ${
                      trackRegions.includes(geo)
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'border-border text-muted-foreground'
                    }`}
                  >
                    {geo}
                  </button>
                ))}
                {(dashboardStats?.geos_with_data || []).length === 0 && (
                  <span className="text-[11px] text-muted-foreground">No GEOs loaded yet</span>
                )}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm mb-4">
              <input
                type="checkbox"
                checked={trackSendDaily}
                onChange={(e) => setTrackSendDaily(e.target.checked)}
              />
              <span>Send me daily updates</span>
            </label>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setTrackModalOpen(false)}
                className="h-8 px-3 rounded-md text-xs bg-background border border-border text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
              <button
                onClick={createTrackedFilter}
                disabled={trackingFilter}
                className="h-8 px-3 rounded-md text-xs bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {trackingFilter ? 'Saving...' : 'Save & Track'}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedEntity && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>}>
        <EntityDetailModal
          entity={selectedEntity}
          timeline={entityTimeline}
          onClose={handleCloseModal}
          onGeoClick={setSelectedGeo}
        />
        </Suspense>
      )}
    </Layout>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/magic" element={<LoginPage />} />
          <Route path="/admin" element={<AdminLoginPage />} />
          <Route
            path="/alerts/unsubscribe"
            element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>}>
                <UnsubscribePage />
              </Suspense>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/budget"
            element={
              <ProtectedRoute>
                <BudgetPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cabinet"
            element={
              <ProtectedRoute>
                <CabinetPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cabinet/brand/:entityId"
            element={
              <ProtectedRoute>
                <BrandDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
